import React from "react";
import styled from "styled-components";
import { Row, Col, Card, Typography, Modal, message } from "antd";
import moment from "moment";
import ManualStockEntryHeader from "./Header/ManualStockEntryHeader";
import StockTable from "./Tables/StockTable";
import { FacilityOption } from "components/FacilitiesSelect/FacilitiesSelect";
import { ManualStockEntryStockType } from "services/BillingEngineService/interfaces/entities/ManualStockEntry";
import useQueryUpdateStockEntries from "./Api/useQueryUpdateStockEntries";
import UpdateStockEntriesForm from "./Forms/UpdateStockEntriesForm";
import colors from "css/colors";
import { GreenButton } from "pages/_common/ShippingCostsPage/_common/Buttons";

const generateRowId = () => new Date().getTime();

const generateEmptyRow = () => ({
  id: generateRowId(),
  organization: undefined,
  stockAmount: undefined,
  notes: "",
  errors: [],
});

function ManualStockEntryPage() {
  const [hasSubmitted, setHasSubmitted] = React.useState(false);

  const [selectedFacility, setSelectedFacility] = React.useState<FacilityOption | undefined>();

  const [selectedStockType, setSelectedStockType] = React.useState<ManualStockEntryStockType | undefined>();

  const [entries, setEntries] = React.useState<any[]>([generateEmptyRow()]);

  function addRow() {
    setEntries((entries: any) => {
      return [...entries, generateEmptyRow()];
    });
  }

  function removeRow(id: number) {
    setEntries((entries: any) => {
      return entries.filter((e: any) => e.id !== id);
    });
  }

  function updateRow(updates: any, id: string) {
    setEntries((entries: any) => {
      return entries.map((entry: any) => {
        if (id === entry.id) {
          return {
            ...entry,
            ...updates,
          };
        }

        return entry;
      });
    });
  }

  const [updateModalActive, setUpdateModalActive] = React.useState(false);
  const [updateStockEntries, { error }] = useQueryUpdateStockEntries();

  // disables updates after 10pm
  const updatesEnabled = moment().hour() < 22;

  function onFacilitySelect(facility: FacilityOption) {
    setSelectedFacility(facility);
  }

  function onStockTypeSelect(stockType: ManualStockEntryStockType) {
    setSelectedStockType(stockType);
  }

  function onUpdatePress() {
    setUpdateModalActive(true);
  }

  function validateForm(entries: any[]) {
    return new Promise((resolve, reject) => {
      let hasError = false;

      if (selectedStockType === undefined || selectedFacility === undefined) {
        hasError = true;
      }

      const validatedEntries = entries.map((entry) => {
        const errors = [];
        if (entry.stockAmount === undefined) {
          hasError = true;
          errors.push("stockAmount");
        }

        if (entry.organization === undefined) {
          hasError = true;
          errors.push("organization");
        }

        return {
          ...entry,
          errors,
        };
      });

      hasError ? reject(validatedEntries) : resolve(validatedEntries);
    });
  }

  function onSave() {
    if (updatesEnabled) {
      validateForm(entries)
        .then((entries: any) => {
          const formattedEntries = entries.map((r: any) => {
            return {
              date: new Date().toUTCString(),
              organizationId: r.organization.id,
              facilityCode: selectedFacility?.code,
              stockType: selectedStockType,
              stockAmount: r.stockAmount,
            };
          });

          updateStockEntries(formattedEntries, {
            onSuccess: () => {
              setEntries([generateEmptyRow()]);
              setUpdateModalActive(false);
            },
          });
          setHasSubmitted(true);
        })
        .catch((erroredEntries) => {
          setEntries(erroredEntries);
          setHasSubmitted(true);
        });
    } else {
      message.info(`Cannot update stock entries after 10pm!`);
    }
  }

  const displayEmptyMessage = !selectedFacility || !selectedStockType;

  const canUpdateStock = Boolean(selectedFacility) && Boolean(selectedStockType);

  return (
    <Row gutter={[0, 28]}>
      <Col span={24}>
        <ManualStockEntryHeader
          {...{
            selectedFacility,
            onFacilitySelect,
            selectedStockType,
            onStockTypeSelect,
            onUpdatePress,
            canUpdateStock,
            updatesEnabled,
          }}
        />
      </Col>

      <Col span={24}>
        <Card>
          {displayEmptyMessage ? (
            <EmptyOptionsContainer>
              <Typography.Title level={2}>No Data Selected</Typography.Title>
              <Typography.Paragraph>Select warehouse and stock type to view stock data.</Typography.Paragraph>
              <GreenButton type="primary" disabled={!updatesEnabled} onClick={() => setUpdateModalActive(true)}>
                Update Stock
              </GreenButton>
            </EmptyOptionsContainer>
          ) : (
            <StockTable {...{ selectedFacility, selectedStockType }} />
          )}
        </Card>

        <Modal
          visible={updateModalActive}
          onOk={onSave}
          okButtonProps={{ disabled: !updatesEnabled }}
          title="Update Stock"
          onCancel={() => setUpdateModalActive(false)}
          width={1100}
        >
          <UpdateStockEntriesForm
            {...{
              selectedFacility,
              onFacilitySelect,
              selectedStockType,
              onStockTypeSelect,
              error,
              entries,
              setEntries,
              addRow,
              removeRow,
              updateRow,
              hasSubmitted,
            }}
          />
        </Modal>
      </Col>
    </Row>
  );
}

const EmptyOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 40px 0;
  min-height: 902px;

  h2 {
    font-weight: bold;
    font-size: 24px;
    line-height: 29px;
    text-transform: capitalize;
    color: ${colors.GREY_DARK};
    display: block;
    margin-bottom: 32px;
  }

  p {
    font-size: 16px;
    line-height: 22px;
    color: ${colors.CHARCOAL};
  }

  button {
    margin-top: 47px;
    width: 167px;
    height: 38px;
    color: ${colors.WHITE};
  }
`;

export default ManualStockEntryPage;
